import { useState, useEffect, useCallback } from 'react';
import {
  FileText, Loader2, AlertTriangle, Sparkles, Download, Trash2, CheckCircle2,
  Calendar, ChevronRight,
} from 'lucide-react';
import {
  getReportTemplates, generateReport, getReportHistory, deleteReport, downloadReport,
} from '../services/api';
import { InfoTip } from '../components/ui';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatBytes(n) {
  if (!Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  return `${(v / 1024 / 1024).toFixed(1)} MB`;
}

export default function ReportsPage() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState('monthly_pnl');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [lastGenerated, setLastGenerated] = useState(null);

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [quarter, setQuarter] = useState(Math.ceil((today.getMonth() + 1) / 3));
  const [startDate, setStartDate] = useState(new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const [tplRes, histRes] = await Promise.all([
        getReportTemplates(),
        getReportHistory({ limit: 50 }).catch(() => ({ data: { reports: [] } })),
      ]);
      setTemplates((tplRes?.data || tplRes)?.templates || []);
      setHistory((histRes?.data || histRes)?.reports || []);
    } catch (e) {
      setError(e?.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function inputsForTemplate() {
    if (selected === 'monthly_pnl')  return { year, month };
    if (selected === 'quarterly')    return { year, quarter };
    if (selected === 'year_end_tax') return { year };
    if (selected === 'custom_range') return { startDate, endDate };
    return {};
  }

  async function onGenerate() {
    try {
      setGenerating(true); setGenError(null); setLastGenerated(null);
      const res = await generateReport({ template: selected, inputs: inputsForTemplate() });
      const data = res?.data || res;
      const report = data?.report || data;
      setLastGenerated(report);
      // Auto-download the newly-generated report
      if (report?.reportId) {
        await downloadReport(report.reportId, `haythix-${report.template}-${report.reportId}.pdf`);
      }
      load(); // refresh history
    } catch (e) {
      setGenError(e?.message || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  }

  async function onDelete(reportId) {
    if (!window.confirm('Delete this report? The PDF will be removed from your archive.')) return;
    try { await deleteReport(reportId); load(); }
    catch (e) { setError(e?.message || 'Delete failed'); }
  }

  const selectedTpl = templates.find(t => t.id === selected);
  // Cover 12 years back (current year → ~2015). Covers any crypto trader's
  // full history from before most major exchanges existed. Years with no
  // trades will return an empty PDF — documented in the disclosures block.
  const yearOptions = Array.from({ length: 12 }, (_, i) => today.getFullYear() - i);

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-indigo-500/10 via-dark-800/80 to-dark-900 rounded-2xl border border-indigo-500/20 p-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-indigo-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-white">Institutional Reports</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 font-mono">Elite · Trader's Suite</span>
            </div>
            <p className="text-xs text-indigo-200/70 mt-1 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Branded PDF reports generated server-side from the same ledger Risk Overview reads. No spreadsheets, no copy-paste.
              <InfoTip side="bottom">Reports reconcile EXACTLY with Risk Overview — same lot ledger. Re-download from History is free; regeneration only needed after new fills. Year-End Tax + Tax Report (Intelligence) = full CPA package.</InfoTip>
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-sm text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {loading && templates.length === 0 ? (
        <div className="text-center py-10 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-indigo-400" />Loading templates...</div>
      ) : (
        <>
          {/* Template picker */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {templates.map(t => {
              const active = selected === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setSelected(t.id)}
                  className={`text-left rounded-xl border p-4 transition ${active ? 'border-indigo-500/60 bg-indigo-500/10' : 'border-dark-700 bg-dark-800/40 hover:border-dark-500'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-white">{t.label}</div>
                    {active && <CheckCircle2 className="w-4 h-4 text-indigo-400" />}
                  </div>
                  <div className="text-[11px] text-gray-400 leading-relaxed">{t.description}</div>
                </button>
              );
            })}
          </div>

          {/* Inputs for selected template */}
          {selectedTpl && (
            <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-5 space-y-4">
              <div className="text-[11px] uppercase tracking-wider text-gray-500 font-mono flex items-center gap-1.5">
                <Calendar className="w-3 h-3" /> Period
              </div>

              {selected === 'monthly_pnl' && (
                <div className="grid grid-cols-2 gap-3">
                  <Select label="Month" value={month} onChange={v => setMonth(Number(v))}
                    options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))} />
                  <Select label="Year" value={year} onChange={v => setYear(Number(v))}
                    options={yearOptions.map(y => ({ value: y, label: String(y) }))} />
                </div>
              )}

              {selected === 'quarterly' && (
                <div className="grid grid-cols-2 gap-3">
                  <Select label="Quarter" value={quarter} onChange={v => setQuarter(Number(v))}
                    options={[1, 2, 3, 4].map(q => ({ value: q, label: `Q${q}` }))} />
                  <Select label="Year" value={year} onChange={v => setYear(Number(v))}
                    options={yearOptions.map(y => ({ value: y, label: String(y) }))} />
                </div>
              )}

              {selected === 'year_end_tax' && (
                <Select label="Tax year" value={year} onChange={v => setYear(Number(v))}
                  options={yearOptions.map(y => ({ value: y, label: String(y) }))} />
              )}

              {selected === 'custom_range' && (
                <div className="grid grid-cols-2 gap-3">
                  <DateInput label="Start date" value={startDate} onChange={setStartDate} />
                  <DateInput label="End date" value={endDate} onChange={setEndDate} />
                </div>
              )}

              {genError && <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-2">{genError}</div>}

              {lastGenerated && (
                <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Generated {lastGenerated.reportId} · {formatBytes(lastGenerated.fileSizeBytes)} in {lastGenerated.elapsedMs}ms — downloading...
                </div>
              )}

              <button
                onClick={onGenerate}
                disabled={generating}
                className="w-full sm:w-auto px-5 py-2.5 rounded-lg bg-indigo-500/20 border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/30 text-sm font-semibold transition flex items-center gap-2 disabled:opacity-50"
              >
                {generating
                  ? <><Loader2 className="w-4 h-4 animate-spin" />Generating PDF...</>
                  : <><FileText className="w-4 h-4" />Generate {selectedTpl.label}<ChevronRight className="w-4 h-4" /></>}
              </button>
            </div>
          )}

          {/* History */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-white">Recent reports ({history.length})</h2>
            </div>
            {history.length === 0 ? (
              <div className="text-center py-8 bg-dark-800/40 border border-dark-700 rounded-2xl text-sm text-gray-400">
                No reports generated yet. Pick a template above and click Generate.
              </div>
            ) : (
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-dark-900/40">
                    <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                      <th className="text-left py-2.5 px-4 font-medium">Generated</th>
                      <th className="text-left py-2.5 px-4 font-medium">Template</th>
                      <th className="text-left py-2.5 px-4 font-medium">Period</th>
                      <th className="text-right py-2.5 px-4 font-medium">Size</th>
                      <th className="text-right py-2.5 px-4 font-medium">Build time</th>
                      <th className="text-right py-2.5 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(r => (
                      <tr key={r.reportId} className="border-t border-dark-700/40">
                        <td className="py-2.5 px-4 text-gray-300">{new Date(r.generatedAt).toLocaleString()}</td>
                        <td className="py-2.5 px-4 text-white">{r.templateLabel}</td>
                        <td className="py-2.5 px-4 text-gray-400 font-mono text-[11px]">{describeInputs(r)}</td>
                        <td className="py-2.5 px-4 text-right text-gray-400">{formatBytes(r.fileSizeBytes)}</td>
                        <td className="py-2.5 px-4 text-right text-gray-400">{r.elapsedMs}ms</td>
                        <td className="py-2.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => downloadReport(r.reportId, `haythix-${r.template}-${r.reportId}.pdf`).catch(e => setError(e.message))}
                              className="p-1.5 rounded bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/25"
                              title="Download PDF"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => onDelete(r.reportId)}
                              className="p-1.5 rounded bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20"
                              title="Delete report"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function describeInputs(r) {
  const i = r.inputs || {};
  if (r.template === 'monthly_pnl')  return `${MONTHS[i.month - 1]} ${i.year}`;
  if (r.template === 'quarterly')    return `Q${i.quarter} ${i.year}`;
  if (r.template === 'year_end_tax') return `Tax year ${i.year}`;
  if (r.template === 'custom_range') return `${i.startDate} → ${i.endDate}`;
  return '—';
}

function Select({ label, value, onChange, options }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 block">{label}</label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-dark-900/60 border border-dark-700 text-white text-sm"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function DateInput({ label, value, onChange }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-gray-500 mb-1 block">{label}</label>
      <input
        type="date"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-dark-900/60 border border-dark-700 text-white text-sm"
      />
    </div>
  );
}
